import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTheme } from '../../context/ThemeContext';
import {
  Add,
  Bolt,
  ContentCopy,
  DeleteOutline,
  EditOutlined,
  History,
  Pause,
  PowerSettingsNew,
  WarningAmber,
} from '@mui/icons-material';
import { Zap } from 'lucide-react';
import agentService from '../../services/agentService';
import composioService from '../../services/composioService';
import triggerService from '../../services/triggerService';
import folderD3Service from '../../services/folderD3Service';
import {
  COMPOSIO_DEFAULT_QUERY,
  buildIngestComposioConfigForEvent,
  coerceSchemaFieldValue,
  extractComposioFromTrigger,
  extractIngestSchemaFieldsForEvent,
  extractSchemaFields,
  getConnectionAccountId,
  getConnectionDisplayName,
  getConnectionToolkitSlug,
  getMergedIngestSetupFields,
  getTriggerAgentId,
  getTriggerTypeEventName,
  getTriggerTypeLabel,
  getTriggerTypeSlug,
  isTriggerActive,
  normalizeTriggerItem,
  normalizeTriggerTypesList,
  parseComposioConfigObject,
} from '../../utils/composioTrigger';
import { parseComposioSetupStatus, isLikelyPollingTrigger, pickComposioInboundUrl } from '../../utils/composioWebhook';
import CompactExecutionLogs from '../shared/CompactExecutionLogs';
import ExecutionLogsDrawer from '../shared/ExecutionLogsDrawer';
import {
  buildAgentExecutionSections,
  filterLogsByDate,
  formatLogDateTime,
  getLogStatusMeta,
  getLogTimeValue,
  groupLogsByDate,
} from '../../utils/executionLogUtils';
import { copyTextToClipboard } from '../../utils/clipboard';
import { getSchemaFieldLabel } from '../shared/FolderIdHelpHint';
import IngestDriveQueryField from '../shared/IngestDriveQueryField';
import {
  readMergedIngestFieldValue,
  writeMergedIngestFieldValue,
} from './triggerFormUtils';
import styles from './AgentTriggers.module.scss';

// Theme dropdown popovers (rendered in a portal) for light/dark consistency.
const SELECT_PROPS = { MenuProps: { PaperProps: { className: styles.menuPaper } } };

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Paused' },
];

const TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'webhook', label: 'Custom webhook' },
  { value: 'composio', label: 'App event' },
];

const CREATE_TRIGGER_TYPES = [
  { value: 'webhook', label: 'Custom webhook' },
  { value: 'composio', label: 'App event' },
];

const LEGACY_TRIGGER_TYPES = [
  { value: 'manual', label: 'Manual (legacy)' },
  { value: 'email', label: 'Email (legacy)' },
];

const formatTriggerTypeLabel = (type) => {
  const normalized = String(type || 'webhook').toLowerCase();
  if (normalized === 'composio') return 'App event';
  if (normalized === 'webhook') return 'Webhook';
  if (normalized === 'manual') return 'Manual';
  if (normalized === 'email') return 'Email';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatEventSlug = (slug = '') =>
  String(slug)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Friendly labels for the log `trigger_source` chip.
const TRIGGER_SOURCE_LABELS = {
  composio: 'App event',
  composio_ingest: 'File → D3',
  composio_mirror_scan: 'Tree scan',
};

const formatTriggerSource = (source) => {
  if (!source) return '';
  return TRIGGER_SOURCE_LABELS[source] || source;
};

// Log `output` may arrive as an object or a JSON string — coerce to a plain object.
const coerceLogObject = (value) => {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

// Turn a mirror-scan log's counters into a one-line human summary.
const buildMirrorScanSummary = (output) => {
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const foldersSeen = num(output.folders_seen);
  const foldersCreated = num(output.folders_created);
  const foldersRenamed = num(output.folders_renamed);
  const filesEnqueued = num(output.files_enqueued);
  const filesDuplicate = num(output.files_skipped_duplicate);
  const filesUnsupported = num(output.files_skipped_unsupported);

  const detail = [];
  if (foldersCreated) detail.push(`${foldersCreated} created`);
  if (foldersRenamed) detail.push(`${foldersRenamed} renamed`);
  detail.push(`${filesEnqueued} new file${filesEnqueued === 1 ? '' : 's'} queued`);
  if (filesDuplicate) detail.push(`${filesDuplicate} already synced`);
  if (filesUnsupported) detail.push(`${filesUnsupported} unsupported skipped`);

  let sentence = `Scanned ${foldersSeen} folder${foldersSeen === 1 ? '' : 's'} — ${detail.join(', ')}.`;
  if (output.truncated) {
    sentence += ' Large tree — the next cycle will continue.';
  }
  return sentence;
};

// Mirror-scan logs get a summary + counters view; everything else uses the agent sections.
const resolveTriggerLogSections = (log) => {
  if (log?.trigger_source === 'composio_mirror_scan') {
    const output = coerceLogObject(log.output);
    const sections = [
      { key: 'scan_summary', title: 'Scan summary', value: buildMirrorScanSummary(output) },
    ];
    if (Object.keys(output).length > 0) {
      sections.push({ key: 'scan_counters', title: 'Counters', value: output });
    }
    if (log?.error_message) {
      sections.push({ key: 'error', title: 'Error', value: log.error_message });
    }
    return sections;
  }
  return buildAgentExecutionSections(log);
};

const defaultForm = {
  // 'agent' = run an agent · 'ingest' = save matched files into a D3 knowledge base.
  intent: 'agent',
  agent_id: '',
  name: '',
  trigger_type: 'webhook',
  query_template: 'Summarize and act on this payload:\n\n{{payload}}',
  require_signature: false,
  connected_account_id: '',
  composio_trigger_slug: '',
  composio_trigger_slugs: [],
  composio_trigger_config_json: '{}',
  composio_config_values: {},
  composio_config_by_event: {},
  trigger_config_json: '',
  ingest_to_dim3: false,
  dim3_folder_id: '',
  // Mirror the whole Drive folder tree (nested subfolders) into nested D3 folders.
  mirror_nested: false,
};

const parseLogDateTimeValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return new Date(value);
  const trimmed = value.trim();
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const normalized = trimmed.replace(' ', 'T');
  return new Date(hasTimezone ? trimmed : normalized);
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = parseLogDateTimeValue(value);
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const parseConfigExtraJson = (raw, requireSignature) => {
  const trimmed = String(raw || '').trim();
  let parsed = {};
  if (trimmed) {
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      throw new Error('Extra trigger config must be valid JSON.');
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Extra trigger config must be a JSON object.');
    }
  }
  return { ...parsed, require_signature: !!requireSignature };
};

const COMPOSIO_CONFIG_KEYS = new Set([
  'composio_trigger_slug',
  'composio_trigger_slugs',
  'connected_account_id',
  'composio_trigger_config',
  'composio_trigger_instance_id',
  'ingest_to_dim3',
  'dim3_folder_id',
]);

// Toolkits whose file events can be ingested into a D3 knowledge base.
// Only Google Drive is wired today; matches "googledrive", "google_drive", etc.
const isFileIngestToolkit = (toolkitSlug) =>
  String(toolkitSlug || '')
    .toLowerCase()
    .replace(/[\s_-]/g, '')
    .includes('googledrive');

// Display names of the storage toolkits that currently support D3 ingestion.
// Single source for the "connect a supported app" empty-state copy — extend this
// (and isFileIngestToolkit) together as more stores are wired.
const FILE_INGEST_TOOLKIT_LABELS = ['Google Drive'];

const splitTriggerConfig = (config) => {
  if (!config || typeof config !== 'object') {
    return { require_signature: false, extraJson: '' };
  }
  const { require_signature: rs, ...rest } = config;
  const extra = {};
  Object.keys(rest).forEach((key) => {
    if (!COMPOSIO_CONFIG_KEYS.has(key)) extra[key] = rest[key];
  });
  const keys = Object.keys(extra);
  const extraJson = keys.length ? JSON.stringify(extra, null, 2) : '';
  return {
    require_signature: Boolean(rs),
    extraJson,
  };
};

const parseComposioTriggerConfig = (raw) => {
  const trimmed = String(raw || '').trim() || '{}';
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Trigger configuration must be a JSON object.');
    }
    return parsed;
  } catch (e) {
    if (e.message?.includes('JSON object')) throw e;
    throw new Error('Trigger configuration must be valid JSON.');
  }
};

const buildComposioTriggerPayload = (form, schemaFields = []) => {
  if (!form.connected_account_id) {
    throw new Error('Select a connected Composio app account.');
  }

  const singleSlug = String(form.composio_trigger_slug || '').trim();
  if (!singleSlug) {
    throw new Error('Select a Composio event type.');
  }

  let composio_trigger_config = {};
  if (schemaFields.length > 0) {
    const values = form.composio_config_values || {};
    schemaFields.forEach((field) => {
      const raw = values[field.key];
      if (raw === undefined || raw === '') {
        if (field.required) {
          throw new Error(`"${field.label}" is required for this event type.`);
        }
        return;
      }
      composio_trigger_config[field.key] = coerceSchemaFieldValue(raw, field.type);
    });
  } else {
    composio_trigger_config = parseComposioTriggerConfig(form.composio_trigger_config_json);
  }

  const payload = {
    composio_trigger_slug: singleSlug,
    connected_account_id: form.connected_account_id,
    composio_trigger_config,
  };

  return payload;
};

const buildIngestTriggerConfigForSlug = (
  form,
  slug,
  composioTypeSchemas,
  configByEvent = {},
) => {
  const schemaData = composioTypeSchemas[slug];
  if (!schemaData) {
    throw new Error('Event configuration is still loading. Please wait and try again.');
  }

  const eventName = getTriggerTypeEventName(schemaData, formatEventSlug(slug));
  const setupFields = extractIngestSchemaFieldsForEvent(schemaData, slug);
  const values = configByEvent[slug] || {};

  setupFields
    .filter((field) => field.required)
    .forEach((field) => {
      const raw = values[field.key];
      if (raw === undefined || String(raw).trim() === '') {
        throw new Error(`"${field.label}" is required for ${eventName}.`);
      }
    });

  const composio_trigger_config = buildIngestComposioConfigForEvent(
    schemaData,
    values,
    setupFields,
  );

  const payload = {
    composio_trigger_slug: slug,
    connected_account_id: form.connected_account_id,
    composio_trigger_config,
    ingest_to_dim3: true,
    dim3_folder_id: Number(form.dim3_folder_id),
  };

  if (form.mirror_nested) {
    const folderId = composio_trigger_config.folder_id;
    if (folderId !== undefined && folderId !== null && String(folderId).trim() !== '') {
      payload.mirror_nested = true;
    }
  }

  return payload;
};

const apiErrMessage = (err, fallback) =>
  err?.response?.data?.detail
    ? typeof err.response.data.detail === 'string'
      ? err.response.data.detail
      : JSON.stringify(err.response.data.detail)
    : err?.message || fallback;

const AgentTriggers = () => {
  const { isDarkMode } = useTheme();
  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: { main: '#e36250' },
          secondary: { main: '#8b5cf6' },
        },
      }),
    [isDarkMode],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const queryAgentId = searchParams.get('agentId');
  const agentViewFilter =
    searchParams.get('view') === 'agent' && queryAgentId ? queryAgentId : null;
  const queryCreate = searchParams.get('create');
  const queryToolkit = searchParams.get('toolkit');

  const [agents, setAgents] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [triggerToDelete, setTriggerToDelete] = useState(null);
  const [deletingTrigger, setDeletingTrigger] = useState(false);

  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logDateFilter, setLogDateFilter] = useState('all');
  const [customLogDate, setCustomLogDate] = useState('');

  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualTrigger, setManualTrigger] = useState(null);
  const [manualQuery, setManualQuery] = useState('');
  const [manualPayload, setManualPayload] = useState('{}');
  const [manualVariables, setManualVariables] = useState('');
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualError, setManualError] = useState('');

  const [composioConnections, setComposioConnections] = useState([]);
  const [composioTriggerTypes, setComposioTriggerTypes] = useState([]);
  const [composioTypeSchema, setComposioTypeSchema] = useState(null);
  const [composioTypeSchemas, setComposioTypeSchemas] = useState({});
  const [loadingComposioConnections, setLoadingComposioConnections] = useState(false);
  const [loadingComposioTypes, setLoadingComposioTypes] = useState(false);
  const [loadingComposioSchema, setLoadingComposioSchema] = useState(false);
  const [composioSetup, setComposioSetup] = useState(null);

  const [d3Folders, setD3Folders] = useState([]);
  const [loadingD3Folders, setLoadingD3Folders] = useState(false);

  const loadAgentsAll = useCallback(async () => {
    const limit = 100;
    let page = 1;
    let totalPages = 1;
    const allAgents = [];

    while (page <= totalPages) {
      const response = await agentService.getAgents(page, limit);
      const pageAgents = response?.agents || [];
      allAgents.push(...pageAgents);
      totalPages = response?.total_pages || 1;
      page += 1;
    }

    return allAgents;
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [list, agentList] = await Promise.all([
        triggerService.listTriggers(),
        loadAgentsAll().catch(() => []),
      ]);
      const normalized = (Array.isArray(list) ? list : []).map(normalizeTriggerItem);
      setTriggers(normalized);
      if (agentList?.length) setAgents(agentList);
    } catch (e) {
      console.error('Failed loading triggers:', e);
      setError(apiErrMessage(e, 'Failed to load triggers. Please refresh.'));
    } finally {
      setLoading(false);
    }
  }, [loadAgentsAll]);

  const loadComposioSetup = useCallback(async () => {
    try {
      const data = await composioService.getSetupStatus();
      let setup = parseComposioSetupStatus(data);

      if (setup.isWebhookMode && !setup.inboundUrl) {
        try {
          const urlData = await composioService.getInboundWebhookUrl();
          setup = { ...setup, inboundUrl: pickComposioInboundUrl(urlData) || setup.inboundUrl };
        } catch {
          // optional
        }
      }

      setComposioSetup(setup);
    } catch {
      setComposioSetup(null);
    }
  }, []);

  const loadComposioConnections = useCallback(async () => {
    setLoadingComposioConnections(true);
    try {
      const res = await composioService.getConnectedApps();
      const list = res?.connections || res?.connected_apps || [];
      const active = list.filter((conn) => {
        const status = conn.status || conn.connection_status;
        return status === 'active' || status === 'connected';
      });
      setComposioConnections(active);
    } catch (e) {
      console.error('Failed loading Composio connections:', e);
      setComposioConnections([]);
    } finally {
      setLoadingComposioConnections(false);
    }
  }, []);

  const loadD3Folders = useCallback(async () => {
    setLoadingD3Folders(true);
    try {
      const res = await folderD3Service.getFolders();
      setD3Folders(Array.isArray(res) ? res : res?.folders || []);
    } catch (e) {
      console.error('Failed loading D3 folders:', e);
      setD3Folders([]);
    } finally {
      setLoadingD3Folders(false);
    }
  }, []);

  const loadComposioTriggerTypes = useCallback(async (toolkitSlug) => {
    if (!toolkitSlug) {
      setComposioTriggerTypes([]);
      return;
    }
    setLoadingComposioTypes(true);
    try {
      const data = await composioService.listTriggerTypes(toolkitSlug);
      setComposioTriggerTypes(normalizeTriggerTypesList(data));
    } catch (e) {
      console.error('Failed loading Composio trigger types:', e);
      setComposioTriggerTypes([]);
    } finally {
      setLoadingComposioTypes(false);
    }
  }, []);

  const loadComposioTriggerSchema = useCallback(async (slug) => {
    if (!slug) {
      setComposioTypeSchema(null);
      return;
    }
    setLoadingComposioSchema(true);
    try {
      const data = await composioService.getTriggerType(slug);
      setComposioTypeSchema(data);
      const schema = data?.config || data?.config_schema || data?.schema;
      const fields = extractSchemaFields(schema);
      setForm((prev) => {
        const existing = parseComposioConfigObject(
          prev.composio_config_values && Object.keys(prev.composio_config_values).length
            ? prev.composio_config_values
            : prev.composio_trigger_config_json,
        );
        const nextValues = { ...existing };
        fields.forEach((field) => {
          if (nextValues[field.key] === undefined && field.default !== undefined) {
            nextValues[field.key] = field.default;
          }
        });
        return {
          ...prev,
          composio_config_values: nextValues,
          composio_trigger_config_json: JSON.stringify(nextValues, null, 2),
        };
      });
    } catch (e) {
      console.error('Failed loading trigger type schema:', e);
      setComposioTypeSchema(null);
    } finally {
      setLoadingComposioSchema(false);
    }
  }, []);

  const loadComposioTriggerSchemas = useCallback(async (slugs = []) => {
    const normalized = slugs.map((s) => String(s).trim()).filter(Boolean);
    if (!normalized.length) {
      setComposioTypeSchemas({});
      return;
    }
    setLoadingComposioSchema(true);
    try {
      const results = await Promise.all(
        normalized.map((slug) => composioService.getTriggerType(slug)),
      );
      const nextSchemas = {};
      normalized.forEach((slug, index) => {
        nextSchemas[slug] = results[index];
      });
      setComposioTypeSchemas(nextSchemas);

      setForm((prev) => {
        const prevByEvent = prev.composio_config_by_event || {};
        const nextByEvent = { ...prevByEvent };
        normalized.forEach((slug, index) => {
          const setupFields = extractIngestSchemaFieldsForEvent(results[index], slug);
          const existing = { ...(nextByEvent[slug] || {}) };
          setupFields.forEach((field) => {
            if (existing[field.key] === undefined && field.default !== undefined) {
              existing[field.key] = field.default;
            }
          });
          nextByEvent[slug] = existing;
        });
        Object.keys(nextByEvent).forEach((slug) => {
          if (!normalized.includes(slug)) delete nextByEvent[slug];
        });
        return {
          ...prev,
          composio_config_by_event: nextByEvent,
        };
      });
    } catch (e) {
      console.error('Failed loading trigger type schemas:', e);
      setComposioTypeSchemas({});
    } finally {
      setLoadingComposioSchema(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadComposioConnections();
  }, [loadData, loadComposioConnections]);

  const selectedToolkitSlug = useMemo(() => {
    if (!form.connected_account_id) return '';
    const conn = composioConnections.find(
      (c) => getConnectionAccountId(c) === form.connected_account_id,
    );
    return getConnectionToolkitSlug(conn);
  }, [form.connected_account_id, composioConnections]);

  useEffect(() => {
    if (form.trigger_type !== 'composio' || !dialogOpen) return;
    loadComposioConnections();
  }, [form.trigger_type, dialogOpen, loadComposioConnections]);

  useEffect(() => {
    if (form.trigger_type !== 'composio' || !dialogOpen || !selectedToolkitSlug) return;
    loadComposioTriggerTypes(selectedToolkitSlug);
  }, [form.trigger_type, dialogOpen, selectedToolkitSlug, loadComposioTriggerTypes]);

  const ingestSupported = useMemo(
    () => isFileIngestToolkit(selectedToolkitSlug),
    [selectedToolkitSlug],
  );

  // In "Save to D3" mode, only connections whose toolkit supports file ingestion
  // (currently Google Drive) are selectable — a Dropbox/other connection must not
  // appear here, or the user could pick a store ingestion can't handle.
  const ingestConnections = useMemo(
    () =>
      composioConnections.filter((conn) =>
        isFileIngestToolkit(getConnectionToolkitSlug(conn)),
      ),
    [composioConnections],
  );

  useEffect(() => {
    if (form.trigger_type !== 'composio' || !dialogOpen || !ingestSupported) return;
    loadD3Folders();
  }, [form.trigger_type, dialogOpen, ingestSupported, loadD3Folders]);

  const ingestModeForEffects = form.intent === 'ingest';

  useEffect(() => {
    if (form.trigger_type !== 'composio' || !dialogOpen) return;
    if (ingestModeForEffects) {
      const slugs = (form.composio_trigger_slugs || []).map((s) => String(s).trim()).filter(Boolean);
      if (!slugs.length) {
        setComposioTypeSchemas({});
        return;
      }
      loadComposioTriggerSchemas(slugs);
      return;
    }
    if (!form.composio_trigger_slug) return;
    loadComposioTriggerSchema(form.composio_trigger_slug);
  }, [
    form.trigger_type,
    form.composio_trigger_slug,
    form.composio_trigger_slugs,
    ingestModeForEffects,
    dialogOpen,
    loadComposioTriggerSchema,
    loadComposioTriggerSchemas,
  ]);

  const connectionLabelMap = useMemo(() => {
    const map = new Map();
    composioConnections.forEach((conn) => {
      const id = getConnectionAccountId(conn);
      if (id) map.set(id, getConnectionDisplayName(conn));
    });
    return map;
  }, [composioConnections]);

  const agentNameMap = useMemo(() => {
    const map = new Map();
    agents.forEach((agent) => map.set(String(agent.id), agent.name || `Agent ${agent.id}`));
    return map;
  }, [agents]);

  const agentScopedTriggers = useMemo(() => {
    if (!agentViewFilter) return triggers;
    return triggers.filter((item) => getTriggerAgentId(item) === String(agentViewFilter));
  }, [triggers, agentViewFilter]);

  const filteredTriggers = useMemo(() => {
    return agentScopedTriggers.filter((item) => {
      const active = isTriggerActive(item);
      const triggerType = String(item.trigger_type || 'webhook').toLowerCase();
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && active) ||
        (statusFilter === 'inactive' && !active);
      const matchesType =
        typeFilter === 'all' ||
        (typeFilter === 'webhook' && triggerType === 'webhook') ||
        (typeFilter === 'composio' && triggerType === 'composio');

      return matchesStatus && matchesType;
    });
  }, [agentScopedTriggers, statusFilter, typeFilter]);

  const statusCounts = useMemo(() => {
    const scoped = agentScopedTriggers.filter((item) => {
      const triggerType = String(item.trigger_type || 'webhook').toLowerCase();
      return (
        typeFilter === 'all' ||
        (typeFilter === 'webhook' && triggerType === 'webhook') ||
        (typeFilter === 'composio' && triggerType === 'composio')
      );
    });
    const counts = { all: scoped.length, active: 0, inactive: 0 };
    scoped.forEach((item) => {
      if (isTriggerActive(item)) counts.active += 1;
      else counts.inactive += 1;
    });
    return counts;
  }, [agentScopedTriggers, typeFilter]);

  const typeCounts = useMemo(() => {
    const scoped = agentScopedTriggers.filter((item) => {
      const active = isTriggerActive(item);
      return (
        statusFilter === 'all' ||
        (statusFilter === 'active' && active) ||
        (statusFilter === 'inactive' && !active)
      );
    });
    const counts = { all: scoped.length, webhook: 0, composio: 0 };
    scoped.forEach((item) => {
      const triggerType = String(item.trigger_type || 'webhook').toLowerCase();
      if (triggerType === 'composio') counts.composio += 1;
      else if (triggerType === 'webhook') counts.webhook += 1;
    });
    return counts;
  }, [agentScopedTriggers, statusFilter]);

  const hasComposioTriggers = useMemo(
    () => triggers.some((t) => t.trigger_type === 'composio'),
    [triggers],
  );

  const composioSchemaFields = useMemo(() => {
    if (form.intent === 'ingest') return [];
    return extractSchemaFields(
      composioTypeSchema?.config ||
        composioTypeSchema?.config_schema ||
        composioTypeSchema?.schema ||
        composioTypeSchema,
    );
  }, [form.intent, composioTypeSchema]);

  const composioEventLabelMap = useMemo(() => {
    const map = new Map();
    composioTriggerTypes.forEach((item) => {
      const slug = getTriggerTypeSlug(item);
      if (slug) map.set(slug, getTriggerTypeLabel(item));
    });
    return map;
  }, [composioTriggerTypes]);

  const mergedIngestSetupFields = useMemo(
    () => getMergedIngestSetupFields(composioTypeSchemas),
    [composioTypeSchemas],
  );

  const selectedIngestSlugs = useMemo(
    () => (form.composio_trigger_slugs || []).map((s) => String(s).trim()).filter(Boolean),
    [form.composio_trigger_slugs],
  );

  const dialogTriggerTypes = useMemo(() => {
    if (!editingTrigger) return CREATE_TRIGGER_TYPES;
    const current = editingTrigger.trigger_type || 'webhook';
    const isLegacy = current === 'manual' || current === 'email';
    return isLegacy
      ? [...CREATE_TRIGGER_TYPES, ...LEGACY_TRIGGER_TYPES.filter((t) => t.value === current)]
      : CREATE_TRIGGER_TYPES;
  }, [editingTrigger]);

  useEffect(() => {
    if (hasComposioTriggers) loadComposioSetup();
  }, [hasComposioTriggers, loadComposioSetup]);

  const clearAgentFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('agentId');
    next.delete('view');
    setSearchParams(next, { replace: true });
  };

  const isFilteredEmpty =
    !loading && triggers.length > 0 && filteredTriggers.length === 0;
  const isTrulyEmpty = !loading && triggers.length === 0;

  const ensureAgentsLoaded = useCallback(async () => {
    if (agents.length > 0) return;
    const data = await loadAgentsAll();
    setAgents(data || []);
  }, [agents.length, loadAgentsAll]);

  // Top-level mode switch. 'ingest' forces the Composio/Google-Drive path and drops
  // the agent + message; 'agent' restores the standard run-an-agent flow. Kept in sync
  // with ingest_to_dim3 so the save/payload logic stays unchanged.
  const handleIntentChange = (nextIntent) => {
    setForm((prev) => {
      if (nextIntent === 'ingest') {
        return {
          ...prev,
          intent: 'ingest',
          trigger_type: 'composio',
          ingest_to_dim3: true,
          agent_id: '',
          require_signature: false,
          query_template: COMPOSIO_DEFAULT_QUERY,
          connected_account_id: '',
          composio_trigger_slug: '',
          composio_trigger_slugs: [],
          composio_trigger_config_json: '{}',
          composio_config_values: {},
          composio_config_by_event: {},
          dim3_folder_id: '',
          mirror_nested: false,
        };
      }
      return {
        ...prev,
        intent: 'agent',
        trigger_type: 'webhook',
        ingest_to_dim3: false,
        dim3_folder_id: '',
        mirror_nested: false,
        query_template: defaultForm.query_template,
        connected_account_id: '',
        composio_trigger_slug: '',
        composio_trigger_slugs: [],
        composio_trigger_config_json: '{}',
        composio_config_values: {},
        composio_config_by_event: {},
      };
    });
    setComposioTriggerTypes([]);
    setComposioTypeSchema(null);
    setComposioTypeSchemas({});
  };

  const handleTriggerTypeChange = (nextType) => {
    setForm((prev) => {
      const base = { ...prev, trigger_type: nextType };
      if (nextType === 'composio' && prev.trigger_type !== 'composio') {
        return {
          ...base,
          query_template: COMPOSIO_DEFAULT_QUERY,
          require_signature: false,
          connected_account_id: '',
          composio_trigger_slug: '',
          composio_trigger_slugs: [],
          composio_trigger_config_json: '{}',
          composio_config_values: {},
          composio_config_by_event: {},
          ingest_to_dim3: false,
          dim3_folder_id: '',
          mirror_nested: false,
        };
      }
      if (prev.trigger_type === 'composio' && nextType !== 'composio') {
        return {
          ...base,
          query_template: defaultForm.query_template,
          connected_account_id: '',
          composio_trigger_slug: '',
          composio_trigger_slugs: [],
          composio_trigger_config_json: '{}',
          composio_config_values: {},
          composio_config_by_event: {},
          ingest_to_dim3: false,
          dim3_folder_id: '',
          mirror_nested: false,
        };
      }
      return base;
    });
    setComposioTriggerTypes([]);
    setComposioTypeSchema(null);
    setComposioTypeSchemas({});
  };

  const openCreate = async (options = {}) => {
    const { composio = false, toolkitSlug = '' } = options;
    await ensureAgentsLoaded();

    let connectionsForPick = composioConnections;
    if (composio) {
      const res = await composioService.getConnectedApps();
      const list = res?.connections || res?.connected_apps || [];
      connectionsForPick = list.filter((conn) => {
        const status = conn.status || conn.connection_status;
        return status === 'active' || status === 'connected';
      });
      setComposioConnections(connectionsForPick);
    }

    let connectedAccountId = '';
    if (composio && toolkitSlug) {
      const conn = connectionsForPick.find(
        (c) => getConnectionToolkitSlug(c) === toolkitSlug.toLowerCase(),
      );
      connectedAccountId = getConnectionAccountId(conn);
    }

    setEditingTrigger(null);
    setComposioTriggerTypes([]);
    setComposioTypeSchema(null);
    setComposioTypeSchemas({});
    setForm({
      ...defaultForm,
      agent_id: queryAgentId || '',
      ...(composio
        ? {
            trigger_type: 'composio',
            query_template: COMPOSIO_DEFAULT_QUERY,
            connected_account_id: connectedAccountId,
            composio_trigger_slug: '',
            composio_trigger_config_json: '{}',
            composio_config_values: {},
          }
        : {}),
    });
    setFormError('');
    setDialogOpen(true);
  };

  // Deep-link from agent editor: ?agentId=1&create=composio&toolkit=gmail
  useEffect(() => {
    if (loading || queryCreate !== 'composio' || dialogOpen) return;

    const run = async () => {
      await loadComposioConnections();
      await openCreate({
        composio: true,
        toolkitSlug: queryToolkit || '',
      });
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      next.delete('toolkit');
      setSearchParams(next, { replace: true });
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, queryCreate]);

  const openEdit = async (trigger) => {
    await ensureAgentsLoaded();
    setEditingTrigger(trigger);
    const split = splitTriggerConfig(trigger.trigger_config);
    const composio = extractComposioFromTrigger(trigger);
    const cfg = trigger.trigger_config || {};
    const ingestSlugs =
      composio.slugs?.length > 0
        ? composio.slugs
        : composio.slug
          ? [composio.slug]
          : [];
    setComposioTriggerTypes([]);
    setComposioTypeSchema(null);
    setComposioTypeSchemas({});
    setForm({
      intent: cfg.ingest_to_dim3 ? 'ingest' : 'agent',
      agent_id: String(trigger.agent_id || ''),
      name: trigger.name || '',
      trigger_type: trigger.trigger_type || 'webhook',
      query_template: trigger.query_template || '',
      require_signature: split.require_signature,
      connected_account_id: composio.connectedAccountId,
      composio_trigger_slug: composio.slug,
      composio_trigger_slugs: ingestSlugs,
      composio_trigger_config_json: composio.configJson || '{}',
      composio_config_values: composio.composioConfig || {},
      composio_config_by_event: ingestSlugs.length
        ? { [ingestSlugs[0]]: composio.composioConfig || {} }
        : {},
      trigger_config_json: split.extraJson,
      ingest_to_dim3: Boolean(cfg.ingest_to_dim3),
      dim3_folder_id:
        cfg.dim3_folder_id === undefined || cfg.dim3_folder_id === null
          ? ''
          : String(cfg.dim3_folder_id),
      mirror_nested: Boolean(cfg.mirror_nested),
    });
    setFormError('');
    setDialogOpen(true);
  };

  const saveTrigger = async () => {
    try {
      setFormError('');
      const isComposio = form.trigger_type === 'composio';
      const ingestOn = isComposio && form.ingest_to_dim3;

      // Agent is only required when the trigger actually runs an agent.
      if (!ingestOn && !form.agent_id) throw new Error('Please choose an agent.');
      if (!form.name.trim()) throw new Error('Please enter a trigger name.');
      // Message is optional when saving matched files to a D3 knowledge base.
      if (!ingestOn && !form.query_template.trim()) {
        throw new Error('Please enter a query template.');
      }
      if (ingestOn && form.dim3_folder_id === '') {
        throw new Error('Select a target D3 folder.');
      }
      if (ingestOn) {
        const slugs = (form.composio_trigger_slugs || []).map((s) => String(s).trim()).filter(Boolean);
        if (loadingComposioSchema) {
          throw new Error('Still loading event configuration. Please wait.');
        }
        const missingSchema = slugs.filter((slug) => !composioTypeSchemas[slug]);
        if (missingSchema.length > 0) {
          throw new Error('Event configuration is still loading. Please wait and try again.');
        }
      }

      let trigger_config;

      if (isComposio && ingestOn) {
        const slugs = (form.composio_trigger_slugs || []).map((s) => String(s).trim()).filter(Boolean);
        const editSlug = editingTrigger
          ? form.composio_trigger_slug || slugs[0] || ''
          : '';
        const slug = editingTrigger ? editSlug : slugs[0] || '';

        if (!slug && !editingTrigger) {
          throw new Error('Select at least one Composio event type.');
        }

        if (editingTrigger) {
          trigger_config = buildIngestTriggerConfigForSlug(
            form,
            slug,
            composioTypeSchemas,
            form.composio_config_by_event,
          );
          const existing = extractComposioFromTrigger(editingTrigger);
          if (existing.instanceId) {
            trigger_config.composio_trigger_instance_id = existing.instanceId;
          }
        }
      } else if (isComposio) {
        trigger_config = buildComposioTriggerPayload(form, composioSchemaFields);
        if (editingTrigger) {
          const existing = extractComposioFromTrigger(editingTrigger);
          if (existing.instanceId) {
            trigger_config.composio_trigger_instance_id = existing.instanceId;
          }
        }
      } else {
        trigger_config = parseConfigExtraJson(form.trigger_config_json, form.require_signature);
      }

      const payload = {
        // Agent is optional in ingest mode; send null when none is chosen.
        agent_id: form.agent_id ? Number(form.agent_id) : null,
        name: form.name.trim(),
        trigger_type: form.trigger_type || 'webhook',
        // No agent message is sent when files are ingested into the knowledge base.
        query_template: ingestOn ? '' : form.query_template.trim(),
        trigger_config,
      };

      setSubmitting(true);
      if (editingTrigger?.id) {
        const updateBody = {
          name: payload.name,
          trigger_type: payload.trigger_type,
          query_template: payload.query_template,
          trigger_config,
        };
        await triggerService.updateTrigger(editingTrigger.id, updateBody);
      } else if (ingestOn) {
        const slugs = (form.composio_trigger_slugs || []).map((s) => String(s).trim()).filter(Boolean);
        const baseName = form.name.trim();
        for (const slug of slugs) {
          const perEventConfig = buildIngestTriggerConfigForSlug(
            form,
            slug,
            composioTypeSchemas,
            form.composio_config_by_event,
          );
          const eventName = getTriggerTypeEventName(
            composioTypeSchemas[slug],
            composioEventLabelMap.get(slug) || formatEventSlug(slug),
          );
          await triggerService.createTrigger({
            agent_id: null,
            name: slugs.length > 1 ? `${baseName} — ${eventName}` : baseName,
            trigger_type: 'composio',
            query_template: '',
            trigger_config: perEventConfig,
          });
        }
      } else {
        await triggerService.createTrigger(payload);
      }

      await loadData();
      setDialogOpen(false);
    } catch (e) {
      setFormError(e.message || apiErrMessage(e, 'Failed to save trigger.'));
    } finally {
      setSubmitting(false);
    }
  };

  const copyText = (text) => {
    copyTextToClipboard(text).catch((e) => {
      console.error('Copy failed:', e);
    });
  };

  const clearLogs = async () => {
    if (!selectedTrigger?.id) return;
    try {
      await triggerService.clearTriggerLogs(selectedTrigger.id);
      setLogs([]);
    } catch (e) {
      console.error('Failed to clear logs:', e);
    }
  };

  const handleToggle = async (trigger) => {
    try {
      await triggerService.toggleTrigger(trigger.id);
      await loadData();
    } catch (e) {
      console.error('Toggle failed:', e);
    }
  };

  const openManualRun = (trigger) => {
    setManualTrigger(trigger);
    setManualQuery('');
    setManualPayload('{}');
    setManualVariables('');
    setManualError('');
    setManualDialogOpen(true);
  };

  const submitManualRun = async () => {
    if (!manualTrigger?.id) return;
    try {
      setManualError('');
      let payload = null;
      const pq = manualQuery.trim();
      const pv = manualPayload.trim();
      const vv = manualVariables.trim();

      if (pv) {
        try {
          payload = JSON.parse(pv);
        } catch {
          throw new Error('Test payload must be valid JSON.');
        }
      }

      let variables = null;
      if (vv) {
        try {
          const parsed = JSON.parse(vv);
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            throw new Error('Variables must be a JSON object.');
          }
          variables = parsed;
        } catch (e) {
          throw e.message ? e : new Error('Variables must be valid JSON.');
        }
      }

      setManualSubmitting(true);
      await triggerService.runTriggerNow(manualTrigger.id, {
        query: pq || null,
        payload,
        variables,
      });
      setManualDialogOpen(false);
      await loadData();
    } catch (e) {
      setManualError(e.message || apiErrMessage(e, 'Run failed.'));
    } finally {
      setManualSubmitting(false);
    }
  };

  const openDeleteDialog = (trigger) => {
    setTriggerToDelete(trigger);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingTrigger) return;
    setDeleteDialogOpen(false);
    setTriggerToDelete(null);
  };

  const confirmDeleteTrigger = async () => {
    if (!triggerToDelete?.id) return;
    try {
      setDeletingTrigger(true);
      await triggerService.deleteTrigger(triggerToDelete.id);
      await loadData();
      setDeleteDialogOpen(false);
      setTriggerToDelete(null);
    } catch (e) {
      console.error('Delete failed:', e);
    } finally {
      setDeletingTrigger(false);
    }
  };

  const loadLogs = useCallback(
    async (trigger = selectedTrigger) => {
      if (!trigger?.id) return;
      setLoadingLogs(true);
      try {
        const response = await triggerService.listTriggerLogs(trigger.id, 100);
        setLogs(Array.isArray(response) ? response : []);
      } catch (e) {
        console.error('Failed to fetch logs:', e);
        setLogs([]);
      } finally {
        setLoadingLogs(false);
      }
    },
    [selectedTrigger],
  );

  const openLogs = async (trigger) => {
    setSelectedTrigger(trigger);
    setLogs([]);
    setLogDateFilter('all');
    setCustomLogDate('');
    setLogsDrawerOpen(true);
    await loadLogs(trigger);
  };

  const groupedLogs = useMemo(() => {
    const filtered = filterLogsByDate(logs, logDateFilter, customLogDate);
    return groupLogsByDate(filtered);
  }, [logs, logDateFilter, customLogDate]);

  // "Save to Knowledge Base" mode: the agent is not run, so agent + message are not required.
  const ingestMode = form.intent === 'ingest';

  // Nested-folder mirroring needs a specific Drive Folder Id (from Event setup) — it may
  // live in the schema-driven values or the advanced JSON. Whole-Drive mirroring isn't allowed.
  const folderIdFilled = useMemo(() => {
    if (ingestMode) {
      return selectedIngestSlugs.some((slug) => {
        const fromEvent = form.composio_config_by_event?.[slug]?.folder_id;
        return fromEvent !== undefined && String(fromEvent).trim() !== '';
      });
    }
    const fromValues = form.composio_config_values?.folder_id;
    if (fromValues !== undefined && String(fromValues).trim() !== '') return true;
    const parsed = parseComposioConfigObject(form.composio_trigger_config_json);
    return parsed.folder_id !== undefined && String(parsed.folder_id).trim() !== '';
  }, [
    ingestMode,
    selectedIngestSlugs,
    form.composio_config_by_event,
    form.composio_config_values,
    form.composio_trigger_config_json,
  ]);

  // Keep the toggle honest: if the Folder Id is cleared, drop mirror_nested so a hidden
  // "on" value can't slip into the payload.
  useEffect(() => {
    if (!folderIdFilled && form.mirror_nested) {
      setForm((p) => ({ ...p, mirror_nested: false }));
    }
  }, [folderIdFilled, form.mirror_nested]);

  return (
    <ThemeProvider theme={muiTheme}>
    <Box className={styles.agentTriggersLayout}>
      <Box className={styles.header}>
        <Box className={styles.titleSection}>
          <Typography variant="h4" component="h1">Triggers</Typography>
          <Typography variant="body2" className={styles.headerSubtitle}>
            Run agents automatically from app events or custom webhooks.
          </Typography>
        </Box>
        <Box className={styles.headerActions}>
          <Button variant="contained" className={styles.addButton} startIcon={<Add />} onClick={() => openCreate()}>
            New Trigger
          </Button>
        </Box>
      </Box>

      <Box className={styles.filterBar}>
        <Box className={styles.filterBarLeft}>
          <Typography className={styles.filterLabel}>Status</Typography>
          <Box className={styles.filterPills}>
            {STATUS_FILTERS.map((filter) => (
              <Button
                key={filter.value}
                size="small"
                disableRipple
                disableTouchRipple
                className={`${styles.filterPill} ${statusFilter === filter.value ? styles.filterPillActive : ''}`}
                onClick={() => setStatusFilter(filter.value)}
              >
                {filter.label}
                <span>{statusCounts[filter.value] ?? 0}</span>
              </Button>
            ))}
          </Box>
          <TextField
            select
            size="small"
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={styles.typeFilterSelect}
            InputLabelProps={{ shrink: true }}
            SelectProps={SELECT_PROPS}
          >
            {TYPE_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label} ({typeCounts[option.value] ?? 0})
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {agentViewFilter && (
          <Chip
            label={`Agent: ${agentNameMap.get(agentViewFilter) || `#${agentViewFilter}`}`}
            onDelete={clearAgentFilter}
            size="small"
            className={styles.agentFilterChip}
          />
        )}
      </Box>

      {isFilteredEmpty && (
        <Alert severity="info" className={styles.filterNotice}>
          {agentViewFilter ? (
            <>
              No triggers for this agent. You have {triggers.length} trigger{triggers.length === 1 ? '' : 's'} overall.{' '}
              <Button size="small" onClick={clearAgentFilter} className={styles.filterNoticeBtn}>
                Show all triggers
              </Button>
            </>
          ) : (
            <>No triggers match the current filters. Try &quot;All&quot; for status or type.</>
          )}
        </Alert>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {hasComposioTriggers && composioSetup && !composioSetup.ready && (
        <Alert severity="warning" className={styles.setupWarning}>
          Composio app events are not ready yet — restart the API server and ensure Celery is running.
        </Alert>
      )}

      {loading ? (
        <Box className={styles.loadingWrap}>
          <CircularProgress />
        </Box>
      ) : isTrulyEmpty || (isFilteredEmpty && !agentViewFilter) ? (
        <Box className={styles.emptyState}>
          <Zap className={styles.emptyIconLucide} size={64} strokeWidth={1.25} aria-hidden />
          <Typography variant="h6">
            {isFilteredEmpty ? 'No triggers match this filter' : 'No triggers yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isFilteredEmpty
              ? 'Change the status filter or create a new trigger.'
              : 'Use New Trigger to run an agent when an app sends an event or your system calls a webhook.'}
          </Typography>
          {!isFilteredEmpty && (
            <Button variant="contained" className={styles.addButton} startIcon={<Add />} onClick={() => openCreate()}>
              Create your first trigger
            </Button>
          )}
        </Box>
      ) : isFilteredEmpty ? null : (
        <Box className={styles.triggerList}>
          {filteredTriggers.map((trigger, index) => {
            const isActive = isTriggerActive(trigger);
            const isComposio = trigger.trigger_type === 'composio';
            const url = isComposio ? '' : trigger.webhook_url || '';
            const composioMeta = isComposio ? extractComposioFromTrigger(trigger) : null;
            const composioAccountLabel =
              composioMeta?.connectedAccountId &&
              connectionLabelMap.get(composioMeta.connectedAccountId);
            const agentLabel = agentNameMap.get(String(trigger.agent_id)) || `Agent #${trigger.agent_id}`;
            const eventLabel = composioMeta?.slugs?.length
              ? composioMeta.slugs.map((slug) => formatEventSlug(slug)).join(', ')
              : composioMeta?.slug
                ? formatEventSlug(composioMeta.slug)
                : '—';
            const accessLabel = isComposio ? 'App event' : 'Webhook URL';
            const accessValue = isComposio
              ? [composioAccountLabel, eventLabel].filter(Boolean).join(' · ')
              : url;

            return (
              <Box
                key={trigger.id ?? `trigger-row-${index}`}
                className={`${styles.triggerRow} ${isActive ? styles.triggerRowActive : styles.triggerRowInactive}`}
              >
                <Box className={styles.triggerMain}>
                  <Box className={styles.rowTitleLine}>
                    <Typography className={styles.triggerName}>{trigger.name}</Typography>
                    <Chip
                      size="small"
                      label={isActive ? 'Active' : 'Paused'}
                      className={isActive ? styles.activeChip : styles.inactiveChip}
                    />
                    <Chip
                      size="small"
                      label={formatTriggerTypeLabel(trigger.trigger_type)}
                      variant="outlined"
                      className={styles.typeChip}
                    />
                  </Box>

                  <Box className={styles.triggerDetailsGrid}>
                    <Box className={styles.detailItem}>
                      <Typography className={styles.detailLabel}>Agent</Typography>
                      <Typography className={styles.detailValue}>{agentLabel}</Typography>
                    </Box>
                    <Box className={styles.detailItem}>
                      <Typography className={styles.detailLabel}>{isComposio ? 'Event' : 'Type'}</Typography>
                      <Typography className={styles.detailValue}>
                        {isComposio ? eventLabel : 'Custom webhook'}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography className={styles.triggerMetaLine}>
                    Last run {formatDateTime(trigger.last_triggered_at)}
                    {' · '}
                    Created {formatDateTime(trigger.created_at)}
                  </Typography>

                  <Box className={styles.triggerAccessRow}>
                    <Typography className={styles.accessLabel}>{accessLabel}</Typography>
                    <Typography className={styles.accessValue} title={accessValue || undefined}>
                      {accessValue || '—'}
                    </Typography>
                    {!isComposio && url && (
                      <Tooltip title="Copy webhook URL">
                        <IconButton
                          size="small"
                          className={styles.accessCopy}
                          onClick={() => copyText(url)}
                          aria-label="Copy webhook URL"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <Box className={styles.rowActions}>
                  <Tooltip title={isActive ? 'Pause' : 'Activate'}>
                    <IconButton size="small" className={styles.iconButton} onClick={() => handleToggle(trigger)}>
                      {isActive ? <Pause fontSize="small" /> : <PowerSettingsNew fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Test run">
                    <IconButton size="small" className={styles.iconButton} onClick={() => openManualRun(trigger)}>
                      <Bolt fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Run history">
                    <IconButton size="small" className={styles.iconButton} onClick={() => openLogs(trigger)}>
                      <History fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" className={styles.iconButton} onClick={() => openEdit(trigger)}>
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      className={`${styles.iconButton} ${styles.deleteButton}`}
                      onClick={() => openDeleteDialog(trigger)}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !submitting && setDialogOpen(false)}
        fullWidth
        maxWidth="md"
        classes={{ paper: styles.dialogPaper }}
      >
        <DialogTitle className={styles.dialogTitle}>{editingTrigger ? 'Edit Trigger' : 'Create Trigger'}</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {formError && <Alert severity="error">{formError}</Alert>}

          <FormControl className={styles.intentSection}>
            <FormLabel className={styles.intentLabel}>What should this trigger do?</FormLabel>
            <RadioGroup
              row
              value={form.intent}
              onChange={(e) => handleIntentChange(e.target.value)}
              className={styles.intentRadioGroup}
            >
              <FormControlLabel
                value="agent"
                disabled={Boolean(editingTrigger)}
                control={<Radio size="small" />}
                label="Run an agent"
                className={`${styles.intentOption} ${form.intent === 'agent' ? styles.intentOptionActive : ''}`}
              />
              <FormControlLabel
                value="ingest"
                disabled={Boolean(editingTrigger)}
                control={<Radio size="small" />}
                label="Save files to a Knowledge Base (D3)"
                className={`${styles.intentOption} ${form.intent === 'ingest' ? styles.intentOptionActive : ''}`}
              />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary" className={styles.eventSetupHint}>
              {ingestMode
                ? 'New files from a connected storage app are added straight to a D3 knowledge base. No agent runs.'
                : 'Run one of your agents when an app event fires or a webhook is called.'}
            </Typography>
          </FormControl>

          <Box className={styles.formGrid}>
            {!ingestMode && (
              <TextField
                select
                label="Agent"
                value={form.agent_id}
                onChange={(e) => setForm((p) => ({ ...p, agent_id: e.target.value }))}
                required
                size="small"
                disabled={Boolean(editingTrigger)}
                className={styles.formField}
                InputLabelProps={{ shrink: true }}
                SelectProps={SELECT_PROPS}
              >
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={String(agent.id)}>
                    {agent.name || `Agent ${agent.id}`}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Trigger name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
              size="small"
              className={styles.formField}
              InputLabelProps={{ shrink: true }}
            />
            {!ingestMode && (
              <TextField
                select
                label="Trigger type"
                value={form.trigger_type}
                onChange={(e) => handleTriggerTypeChange(e.target.value)}
                size="small"
                className={styles.formField}
                InputLabelProps={{ shrink: true }}
                SelectProps={SELECT_PROPS}
              >
                {dialogTriggerTypes.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          {form.trigger_type === 'composio' && (
            <Box className={styles.composioFormSection}>
              {ingestMode && !loadingComposioConnections && ingestConnections.length === 0 && (
                <Alert severity="info" className={styles.composioBannerAlert}>
                  No compatible storage account connected. Connect a supported storage app
                  ({FILE_INGEST_TOOLKIT_LABELS.join(', ')}) on the Tools page to use ingestion.
                </Alert>
              )}
              <TextField
                select
                label="Connected account"
                value={form.connected_account_id}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    connected_account_id: e.target.value,
                    composio_trigger_slug: '',
                    composio_trigger_slugs: [],
                    composio_trigger_config_json: '{}',
                    composio_config_values: {},
                    composio_config_by_event: {},
                  }))
                }
                required
                size="small"
                fullWidth
                disabled={loadingComposioConnections || Boolean(editingTrigger)}
                className={styles.formField}
                InputLabelProps={{ shrink: true }}
                SelectProps={SELECT_PROPS}
                helperText={
                  loadingComposioConnections
                    ? 'Loading connections…'
                    : (ingestMode ? ingestConnections : composioConnections).length === 0
                      ? ingestMode
                        ? 'Connect a supported storage app on the Tools page first.'
                        : 'No active connections. Connect an app on the Tools page.'
                      : editingTrigger
                        ? 'Account cannot be changed after the instance is provisioned.'
                        : ingestMode
                          ? 'Files added to this account are ingested into the chosen D3 folder.'
                          : ' '
                }
              >
                {(ingestMode ? ingestConnections : composioConnections).map((conn) => {
                  const id = getConnectionAccountId(conn);
                  return (
                    <MenuItem key={id} value={id}>
                      {getConnectionDisplayName(conn)}
                      {getConnectionToolkitSlug(conn) ? ` (${getConnectionToolkitSlug(conn)})` : ''}
                    </MenuItem>
                  );
                })}
              </TextField>

              {ingestMode ? (
                <TextField
                  select
                  label="Event type"
                  value={selectedIngestSlugs}
                  onChange={(e) => {
                    const value = e.target.value;
                    const slugs = typeof value === 'string' ? value.split(',') : value;
                    setForm((p) => {
                      const prevByEvent = p.composio_config_by_event || {};
                      const nextByEvent = {};
                      slugs.forEach((slug) => {
                        nextByEvent[slug] = prevByEvent[slug] || {};
                      });
                      return {
                        ...p,
                        composio_trigger_slugs: slugs,
                        composio_trigger_slug: slugs[0] || '',
                        composio_config_by_event: nextByEvent,
                      };
                    });
                  }}
                  required
                  size="small"
                  fullWidth
                  disabled={
                    !form.connected_account_id ||
                    loadingComposioTypes ||
                    Boolean(editingTrigger)
                  }
                  className={styles.formField}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    ...SELECT_PROPS,
                    multiple: true,
                    renderValue: (selected) => (
                      <Box className={styles.eventTypeChips}>
                        {selected.map((slug) => (
                          <Chip
                            key={slug}
                            label={composioEventLabelMap.get(slug) || formatEventSlug(slug)}
                            size="small"
                          />
                        ))}
                      </Box>
                    ),
                  }}
                  helperText={
                    loadingComposioTypes
                      ? 'Loading event types…'
                      : !form.connected_account_id
                        ? 'Select a connected account first.'
                        : composioTriggerTypes.length === 0
                          ? 'No trigger types for this toolkit.'
                          : editingTrigger
                            ? 'Event types are fixed after create.'
                            : selectedIngestSlugs.length > 1
                              ? 'Creates one trigger per selected event.'
                              : 'Select one or more file events to watch.'
                  }
                >
                  {composioTriggerTypes.map((item) => {
                    const slug = getTriggerTypeSlug(item);
                    return (
                      <MenuItem key={slug} value={slug}>
                        {getTriggerTypeLabel(item)}
                      </MenuItem>
                    );
                  })}
                </TextField>
              ) : (
                <TextField
                  select
                  label="Event type"
                  value={form.composio_trigger_slug}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      composio_trigger_slug: e.target.value,
                      composio_trigger_config_json: '{}',
                      composio_config_values: {},
                    }))
                  }
                  required
                  size="small"
                  fullWidth
                  disabled={
                    !form.connected_account_id ||
                    loadingComposioTypes ||
                    Boolean(editingTrigger)
                  }
                  className={styles.formField}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={SELECT_PROPS}
                  helperText={
                    loadingComposioTypes
                      ? 'Loading event types…'
                      : !form.connected_account_id
                        ? 'Select a connected account first.'
                        : composioTriggerTypes.length === 0
                          ? 'No trigger types for this toolkit.'
                          : editingTrigger
                            ? 'Event type is fixed after create.'
                            : ' '
                  }
                >
                  {composioTriggerTypes.map((item) => {
                    const slug = getTriggerTypeSlug(item);
                    return (
                      <MenuItem key={slug} value={slug}>
                        {getTriggerTypeLabel(item)}
                      </MenuItem>
                    );
                  })}
                </TextField>
              )}

              {(ingestMode
                ? selectedIngestSlugs.some((slug) => isLikelyPollingTrigger(slug))
                : form.composio_trigger_slug && isLikelyPollingTrigger(form.composio_trigger_slug)) && (
                <Typography variant="caption" color="text.secondary" className={styles.pollHint}>
                  Poll-based event — may take up to 15 minutes to detect.
                </Typography>
              )}

              {(ingestMode ? selectedIngestSlugs.length > 0 : Boolean(form.composio_trigger_slug)) && (
                <Box className={styles.eventSetupSection}>
                  <Typography variant="subtitle2" className={styles.eventSetupTitle}>
                    Event setup
                  </Typography>
                  <Typography variant="caption" color="text.secondary" className={styles.eventSetupHint}>
                    {loadingComposioSchema
                      ? 'Loading required fields…'
                      : ingestMode
                        ? mergedIngestSetupFields.length > 0
                          ? 'Set options for the selected events. Polling interval and result limits use defaults automatically.'
                          : 'No extra setup required for the selected events.'
                        : composioSchemaFields.length > 0
                          ? 'Fill in the options below so Composio knows what to watch (e.g. which repo, label, or channel).'
                          : 'No extra setup required for this event type.'}
                  </Typography>

                  {ingestMode ? (
                    mergedIngestSetupFields.length > 0 ? (
                      <Box className={styles.schemaFieldsGrid}>
                        {mergedIngestSetupFields.map(({ field, slugs }) => {
                          if (field.key === 'query') {
                            return (
                              <IngestDriveQueryField
                                key={field.key}
                                field={field}
                                value={readMergedIngestFieldValue(
                                  form.composio_config_by_event,
                                  slugs,
                                  'query',
                                )}
                                onChange={(value) => {
                                  setForm((p) => ({
                                    ...p,
                                    composio_config_by_event: writeMergedIngestFieldValue(
                                      p.composio_config_by_event,
                                      slugs,
                                      'query',
                                      value,
                                    ),
                                  }));
                                }}
                                disabled={loadingComposioSchema}
                                className={styles.formField}
                              />
                            );
                          }

                          return (
                          <TextField
                            key={field.key}
                            label={getSchemaFieldLabel(field)}
                            value={readMergedIngestFieldValue(
                              form.composio_config_by_event,
                              slugs,
                              field.key,
                            )}
                            onChange={(e) => {
                              const value = e.target.value;
                              setForm((p) => ({
                                ...p,
                                composio_config_by_event: writeMergedIngestFieldValue(
                                  p.composio_config_by_event,
                                  slugs,
                                  field.key,
                                  value,
                                ),
                              }));
                            }}
                            select={Boolean(field.enum?.length)}
                            fullWidth
                            size="small"
                            className={styles.formField}
                            InputLabelProps={{ shrink: true }}
                            SelectProps={SELECT_PROPS}
                            helperText={field.description || undefined}
                            disabled={loadingComposioSchema}
                          >
                            {field.enum?.map((opt) => (
                              <MenuItem key={String(opt)} value={String(opt)}>
                                {String(opt)}
                              </MenuItem>
                            ))}
                          </TextField>
                          );
                        })}
                      </Box>
                    ) : null
                  ) : composioSchemaFields.length > 0 ? (
                    <Box className={styles.schemaFieldsGrid}>
                      {composioSchemaFields.map((field) => (
                        <TextField
                          key={field.key}
                          label={getSchemaFieldLabel(field)}
                          value={form.composio_config_values?.[field.key] ?? ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForm((p) => {
                              const nextValues = { ...(p.composio_config_values || {}), [field.key]: value };
                              return {
                                ...p,
                                composio_config_values: nextValues,
                                composio_trigger_config_json: JSON.stringify(nextValues, null, 2),
                              };
                            });
                          }}
                          select={Boolean(field.enum?.length)}
                          fullWidth
                          size="small"
                          className={styles.formField}
                          InputLabelProps={{ shrink: true }}
                          SelectProps={SELECT_PROPS}
                          helperText={field.description || undefined}
                          disabled={loadingComposioSchema}
                        >
                          {field.enum?.map((opt) => (
                            <MenuItem key={String(opt)} value={String(opt)}>
                              {String(opt)}
                            </MenuItem>
                          ))}
                        </TextField>
                      ))}
                    </Box>
                  ) : !loadingComposioSchema ? (
                    <TextField
                      label="Advanced settings (JSON, optional)"
                      value={form.composio_trigger_config_json}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          composio_trigger_config_json: e.target.value,
                          composio_config_values: parseComposioConfigObject(e.target.value),
                        }))
                      }
                      fullWidth
                      multiline
                      rows={3}
                      className={styles.formField}
                      InputLabelProps={{ shrink: true }}
                      placeholder="{}"
                      helperText="Only needed if your event type requires custom parameters."
                    />
                  ) : null}
                </Box>
              )}
            </Box>
          )}

          {ingestMode && ingestSupported && (
            <Box className={styles.ingestSection}>
              <Typography variant="subtitle2" className={styles.eventSetupTitle}>
                Knowledge Base destination
              </Typography>
              <Typography variant="caption" color="text.secondary" className={styles.eventSetupHint}>
                New files matching this event are added to the selected D3 knowledge base. Only
                supported file types are ingested (pdf, doc, txt, csv, images, etc.); others are
                skipped. Ingestion runs on the trigger poll interval, so it may take a few minutes.
              </Typography>

              <TextField
                select
                label="Target D3 folder"
                value={form.dim3_folder_id}
                onChange={(e) =>
                  setForm((p) => ({ ...p, dim3_folder_id: e.target.value }))
                }
                required
                size="small"
                fullWidth
                disabled={loadingD3Folders}
                className={styles.formField}
                InputLabelProps={{ shrink: true }}
                SelectProps={SELECT_PROPS}
                helperText={
                  loadingD3Folders
                    ? 'Loading D3 folders…'
                    : d3Folders.length === 0
                      ? 'No D3 folders yet. Create one in Knowledge Base (D3) first.'
                      : ' '
                }
              >
                {d3Folders.map((folder) => (
                  <MenuItem key={folder.id} value={String(folder.id)}>
                    {folder.name || `Folder ${folder.id}`}
                  </MenuItem>
                ))}
              </TextField>

              {folderIdFilled && (
                <>
                  <FormControlLabel
                    className={styles.formSwitch}
                    control={
                      <Switch
                        checked={form.mirror_nested}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, mirror_nested: e.target.checked }))
                        }
                        color="primary"
                      />
                    }
                    label="Include nested folders"
                  />
                  <Typography variant="caption" color="text.secondary" className={styles.eventSetupHint}>
                    Also mirror all subfolders of the watched Drive folder. Matching folders are
                    created automatically inside the selected D3 folder and all supported files are
                    added — including files already in the folder. New nested files are picked up
                    every few minutes.
                  </Typography>
                </>
              )}
            </Box>
          )}

          {!ingestMode && form.trigger_type === 'webhook' && (
            <FormControlLabel
              className={styles.formSwitch}
              control={
                <Switch
                  checked={form.require_signature}
                  onChange={(e) => setForm((p) => ({ ...p, require_signature: e.target.checked }))}
                  color="primary"
                />
              }
              label="Verify webhook signature (optional)"
            />
          )}

          {/* When saving files to D3, the agent never runs — hide the message field entirely. */}
          {!ingestMode && (
            <TextField
              label="Message to send the agent"
              value={form.query_template}
              onChange={(e) => setForm((p) => ({ ...p, query_template: e.target.value }))}
              required
              fullWidth
              multiline
              rows={5}
              className={styles.formField}
              InputLabelProps={{ shrink: true }}
              helperText={
                form.trigger_type === 'composio'
                  ? 'What the agent should do when the event fires.'
                  : 'What the agent should do. Use {{payload}} to include webhook data.'
              }
            />
          )}
        </DialogContent>
        <DialogActions className={styles.formDialogActions}>
          <Button onClick={() => setDialogOpen(false)} className={styles.cancelButton}>Cancel</Button>
          <Button onClick={saveTrigger} disabled={submitting} className={styles.addButton} variant="contained">
            {submitting ? <CircularProgress size={18} color="inherit" /> : editingTrigger ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manualDialogOpen} onClose={() => !manualSubmitting && setManualDialogOpen(false)} fullWidth maxWidth="sm" classes={{ paper: styles.dialogPaper }}>
        <DialogTitle className={styles.dialogTitle}>Test run — {manualTrigger?.name || ''}</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {manualError && <Alert severity="error">{manualError}</Alert>}
          <Typography className={styles.hintText}>
            Simulates inbound JSON. Leave query empty to render from the template. Variables are passed to the agent execution API when set.
          </Typography>
          <TextField
            label="Query override (optional)"
            value={manualQuery}
            onChange={(e) => setManualQuery(e.target.value)}
            fullWidth
            multiline
            rows={2}
            className={styles.formField}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Payload JSON (optional)"
            value={manualPayload}
            onChange={(e) => setManualPayload(e.target.value)}
            fullWidth
            multiline
            rows={4}
            className={styles.formField}
            InputLabelProps={{ shrink: true }}
            placeholder="{}"
          />
          <TextField
            label="Agent variables JSON (optional)"
            value={manualVariables}
            onChange={(e) => setManualVariables(e.target.value)}
            fullWidth
            multiline
            rows={3}
            className={styles.formField}
            InputLabelProps={{ shrink: true }}
            placeholder='{"customer_name": "Jane"}'
          />
        </DialogContent>
        <DialogActions className={styles.formDialogActions}>
          <Button onClick={() => setManualDialogOpen(false)} disabled={manualSubmitting} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button onClick={submitManualRun} disabled={manualSubmitting} className={styles.addButton} variant="contained">
            {manualSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Run now'}
          </Button>
        </DialogActions>
      </Dialog>

      <ExecutionLogsDrawer
        open={logsDrawerOpen}
        onClose={() => setLogsDrawerOpen(false)}
        title="Trigger runs"
        subtitle={selectedTrigger?.name || 'Trigger'}
        loading={loadingLogs}
        onRefresh={() => loadLogs()}
        onClear={clearLogs}
        refreshDisabled={!selectedTrigger}
        clearDisabled={!selectedTrigger || logs.length === 0}
      >
        <CompactExecutionLogs
          groupedLogs={groupedLogs}
          loading={loadingLogs}
          emptyMessage="No executions logged yet."
          filteredEmptyMessage="No runs in this range."
          showDateFilters
          logDateFilter={logDateFilter}
          onLogDateFilterChange={setLogDateFilter}
          customLogDate={customLogDate}
          onCustomLogDateChange={setCustomLogDate}
          resetKey={selectedTrigger?.id}
          resolveLogId={(log, index) => log.id || `${getLogTimeValue(log) || 'run'}-${index}`}
          resolveHeader={(log) => ({
            statusMeta: getLogStatusMeta(log.status),
            source: formatTriggerSource(log.trigger_source),
            time: formatLogDateTime(getLogTimeValue(log)),
            error: log.error_message,
          })}
          resolveSections={resolveTriggerLogSections}
        />
      </ExecutionLogsDrawer>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} fullWidth maxWidth="sm" classes={{ paper: styles.dialogPaper }}>
        <DialogTitle className={styles.dialogTitle}>Delete trigger</DialogTitle>
        <DialogContent className={styles.deleteDialogContent}>
          <Box className={styles.warningIconWrap}>
            <WarningAmber />
          </Box>
          <Typography className={styles.deleteDialogHeading}>Delete this trigger?</Typography>
          <Typography className={styles.deleteDialogText}>
            Inbound webhooks to "{triggerToDelete?.name || 'this trigger'}" will stop working. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={closeDeleteDialog} disabled={deletingTrigger} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteTrigger}
            disabled={deletingTrigger}
            variant="contained"
            className={styles.dangerButton}
            startIcon={deletingTrigger ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
          >
            {deletingTrigger ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ThemeProvider>
  );
};

export default AgentTriggers;
